import React, { useEffect, useState } from 'react';
import { db, auth } from './firebase';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';

const TripTrackingScreen = () => {
  const { state } = useLocation();
  const rideId = state?.rideId;
  const navigate = useNavigate();

  const [rideData, setRideData] = useState(null);
  const [rideStatus, setRideStatus] = useState('pending');
  const [driverId, setDriverId] = useState(null);
  const [driverData, setDriverData] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [etaToPickup, setEtaToPickup] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [showVehicleImage, setShowVehicleImage] = useState(false);

  
  const vehicleImageUrl =
    'https://firebasestorage.googleapis.com/v0/b/u-go-taxi-service.firebasestorage.app/o/driver_vehicles%2FqAFysgbCamTFcXS65qvys1lHzRa2%2Fcar_image.jpeg.jpeg?alt=media&token=05c1bd02-a5a5-48ff-a27d-158120d0d9b4';

  useEffect(() => {
    if (!rideId) return;

    const unsubRide = onSnapshot(doc(db, 'riders', rideId), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setRideData(data);
        setRideStatus(data.status || 'pending');

        const assigned = data.assignedDriverId;
        if (assigned && assigned !== driverId) {
          setDriverId(assigned);
        } else if (!assigned) {
          setDriverData(null);
          setDriverLocation(null);
          setEtaToPickup(null);
        }
      } else {
        alert('This ride no longer exists.');
        navigate('/');
      }
    });

    return () => unsubRide();
  }, [driverId, navigate, rideId]);

  useEffect(() => {
    if (!driverId) return;

    const unsubDriver = onSnapshot(doc(db, 'drivers', driverId), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setDriverData(data);

        const loc = data.location;
        if (loc?.latitude && loc?.longitude) {
          setDriverLocation({ lat: loc.latitude, lng: loc.longitude });
        }
      }
    });

    return () => unsubDriver();
  }, [driverId]);

  useEffect(() => {
  let interval;

  const fetchETA = (originCoords, destCoords) => {
    if (!window.google || !window.google.maps) {
      console.warn('Google Maps JS API not loaded yet');
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();

    directionsService.route(
      {
        origin: new window.google.maps.LatLng(originCoords.lat, originCoords.lng),
        destination: new window.google.maps.LatLng(destCoords.lat, destCoords.lng),
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === 'OK') {
          const eta = result.routes?.[0]?.legs?.[0]?.duration?.text;
          setEtaToPickup(eta || null);
        } else {
          console.error('DirectionsService error:', status);
          setEtaToPickup(null);
        }
      }
    );
  };

  if (driverLocation && rideData?.pickupLatLng) {
    const pickup = rideData.pickupLatLng;
    const origin = { lat: driverLocation.lat, lng: driverLocation.lng };
    const destination = { lat: pickup.latitude, lng: pickup.longitude };

    fetchETA(origin, destination);
    interval = setInterval(() => fetchETA(origin, destination), 30000);
  }

  return () => clearInterval(interval);
}, [driverLocation, rideData?.pickupLatLng]);

  const handleCancelRide = async () => {
    try {
      await updateDoc(doc(db, 'riders', rideId), {
        status: 'cancelled_by_passenger',
        statusUpdatedAt: new Date()
      });
      navigate('/');
    } catch (err) {
      alert('Failed to cancel ride.');
    }
  };

  const getStatusText = () => {
    switch (rideStatus) {
      case 'pending': return 'Searching for Drivers';
      case 'assigned': return 'A Driver Has Been Assigned';
      case 'on_route': return 'Driver On Route To Pick Up Location';
      case 'arrived': return 'Driver Has Arrived At Pick Up Location';
      case 'completed': return 'Trip Complete';
      case 'cancelled_by_driver':
      case 'cancelled_by_passenger': return 'Ride Cancelled';
      default: return rideStatus.toUpperCase().replaceAll('_', ' ');
    }
  };

  const statusColor = rideStatus.includes('cancelled') ? '#c0392b' : '#1abc9c';

  return (
    <div style={{ maxWidth: 480, margin: '2rem auto', padding: '1rem' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '1rem' }}>Trip Tracking</h2>

      <div style={{
        backgroundColor: statusColor,
        padding: '1rem',
        color: '#fff',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '1.2rem',
        borderRadius: '8px',
        marginBottom: '1.2rem'
      }}>
        {getStatusText()}
      </div>

      {['assigned', 'on_route'].includes(rideStatus) && etaToPickup && (
        <div style={{
          marginBottom: '1rem',
          fontSize: '1rem',
          fontWeight: 500,
          color: '#2c3e50',
          textAlign: 'center'
        }}>
          Driver ETA: {etaToPickup}
        </div>
      )}

      <div style={{
        background: '#f9f9f9',
        borderRadius: '12px',
        padding: '1rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <p><strong>Pickup:</strong> {rideData?.pickupAddress || '...'}</p>
        <p><strong>Drop-off:</strong> {rideData?.dropoffAddress || '...'}</p>
        <p><strong>Booking Type:</strong> {rideData?.bookingMode || '...'}</p>
        <p><strong>Scheduled Time:</strong> {rideData?.scheduledTime ? rideData.scheduledTime.toDate().toLocaleString(): '...'}</p>


       
        {driverData && (
          <>
            <hr />
            <p><strong>Driver:</strong> {driverData.driverName || '...'}</p>
            <p><strong>Vehicle:</strong> {driverData.driverVehicle || '...'}</p>
            <p><strong>ERYC Plate:</strong> {driverData.erycplateNumber || '...'}</p>
            <p><strong>ERYC Licence:</strong> {driverData.erycvehiclelicenceNumber || '...'}</p>
          </>
        )}
      </div>

      {['assigned', 'on_route'].includes(rideStatus) && driverLocation && (
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <button
            onClick={() => setShowMap(prev => !prev)}
            style={{
              padding: '0.7rem 1.5rem',
              fontSize: '1rem',
              backgroundColor: '#2c3e50',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              marginBottom: '1rem'
            }}
          >
            {showMap ? 'Hide Driver Map' : 'View Driver on Map'}
          </button>

          <button
            onClick={() => setShowVehicleImage(true)}
            style={{
              padding: '0.7rem 1.5rem',
              fontSize: '1rem',
              backgroundColor: '#2980b9',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            View Vehicle Image
          </button>
        </div>
      )}

      {showMap && driverLocation && (
        <div style={{ height: '250px', marginTop: '1rem', borderRadius: '12px', overflow: 'hidden' }}>
          <iframe
            title="Driver Map"
            width="100%"
            height="100%"
            frameBorder="0"
            style={{ border: 0 }}
            src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBKQaifAZ7DdD62JkJXq8lBsHxHPr-mGxw&q=${driverLocation.lat},${driverLocation.lng}&zoom=15`}
            allowFullScreen
          />
        </div>
      )}

      {showVehicleImage && (
        <div
          onClick={() => setShowVehicleImage(false)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}
        >
        <img
            src={vehicleImageUrl}
            alt="Vehicle"
            style={{
              maxWidth: '90%',
              maxHeight: '80%',
              borderRadius: 10,
              boxShadow: '0 0 20px rgba(0,0,0,0.5)'
            }}
            
          />
        </div>
      )}
      {['pending', 'assigned', 'on_route'].includes(rideStatus) && (
  <div style={{ marginTop: '2rem', textAlign: 'center' }}>
    <button
      onClick={handleCancelRide}
      style={{
        padding: '0.9rem 1.5rem',
        fontSize: '1rem',
        backgroundColor: '#e74c3c',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer'
      }}
    >
      Cancel Ride
    </button>
  </div>
)}

{rideStatus === 'cancelled' && (
  <div style={{ textAlign: 'center', marginTop: '2rem' }}>
    <div style={{
      backgroundColor: '#fce4e4',
      padding: '1.5rem',
      borderRadius: '12px',
      boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
      maxWidth: 400,
      margin: '0 auto'
    }}>
      <span role="img" aria-label="sad" style={{ fontSize: '3rem' }}>😕</span>
      <h3 style={{ margin: '1rem 0 0.5rem' }}>We couldn’t assign a driver</h3>
      <p style={{ color: '#555', marginBottom: '1.5rem' }}>
        {rideData?.declineReason
          ? `Reason: ${rideData.declineReason}`
          : "Unfortunately, no drivers were available at this time."}
      </p>
      <button
        onClick={async () => {
          await auth.signOut();
          navigate('/');
        }}
        style={{
          backgroundColor: '#008080',
          color: '#fff',
          padding: '0.8rem 1.5rem',
          border: 'none',
          borderRadius: '6px',
          fontSize: '1rem',
          cursor: 'pointer'
        }}
      >
        Log Out
      </button>
    </div>
  </div>
)}

{rideStatus === 'completed' && (
  <div style={{ marginTop: '2rem', textAlign: 'center' }}>
    <button
      onClick={() => window.open('https://g.page/r/CZVgG-HstYV_EAI/review', '_blank')}
      style={{
        marginRight: '1rem',
        backgroundColor: '#f1c40f',
        color: '#000',
        padding: '0.8rem 1.5rem',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer'
      }}
    >
      Leave Review
    </button>
    <button
      onClick={async () => {
        await auth.signOut();
        navigate('/');
      }}
      style={{
        backgroundColor: '#34495e',
        color: '#fff',
        padding: '0.8rem 1.5rem',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer'
      }}
    >
      Log Out
    </button>
  </div>
)}

    </div>
  );
};

export default TripTrackingScreen;

